<template>
	<div class="l-app">
		<CookieConsent v-if="cookieConsentEnabled" />

		<Header
			v-if="showHeader"
			:event-center-enabled="eventCenterEnabled"
			:top-nav-items="topNavItems"
		/>
		<NavMobileTop
			v-if="showHeader && topNavItems?.length && $arenaConfig.prominentMobileTopNav"
			:top-nav-items="topNavItems"
		/>
		<ArenaInfo
			v-if="($arenaConfig.arenaInfos instanceof Array)"
			:current-language-key="$arenaConfig.currentLanguageKey"
			:infos="$arenaConfig.arenaInfos"
		/>
		<div
			class="l-app__main js-app-content"
			data-testid="app-content"
		>
			<main class="l-app__content">
				<slot />
			</main>
		</div>

		<SponsorsStv v-if="$arenaConfig.isStvFsg && showFooterSponsors" />
		<SponsorsPhygital v-if="$arenaConfig.isPhygital && showFooterSponsors" />
		<FooterSponsors
			v-if="$arenaConfig.isSwissVolley && !$arenaConfig.hideSponsorsOnRoutes.includes(pathName) && showFooterSponsors"
			:sponsors="$I18n.trans('arena.swiss_volley_sponsors')"
			modifier="-swiss-volley"
		/>
		<FooterSponsors
			v-if="$arenaConfig.isSportAustria && showFooterSponsors"
			:sponsors="$I18n.trans('arena.sport_austria_sponsors')"
			modifier="-sport-austria"
		/>
		<FooterSponsors
			v-if="$arenaConfig.isOetv && showFooterSponsors"
			:sponsors="$I18n.trans('arena.oetv_sponsors')"
			modifier="-oetv"
		/>
		<FooterSponsors
			v-if="$arenaConfig.domainKey === 'oeoc' && showFooterSponsors"
			:sponsors="$I18n.trans('arena.oeoc_sponsors')"
			modifier="-oeoc"
		/>
		<FooterSponsors
			v-if="$arenaConfig.domainKey === 'swiss-faustball' && showFooterSponsors"
			:sponsors="$I18n.trans('arena.swiss_faustball_sponsors')"
			modifier="-swiss-faustball"
			blend-logos
		/>
		<FooterSponsors
			v-if="$arenaConfig.domainKey === 'swiss-athletics' && showFooterSponsors"
			:sponsors="$I18n.trans('arena.swiss_athletics_sponsors')"
			modifier="-swiss-athletics"
		/>
		<div
			v-if="!$arenaConfig.isSmartTv && showFooter"
			class="l-app__footer"
		>
			<div
				v-if="$arenaConfig.isKrone"
				class="krn-embed krn-embed-footer krone-footer"
				:class="{
					'is-hidden-on-event': !$arenaConfig.showFooterOnEvent,
				}"
			/>
			<Footer
				v-if="!$arenaConfig.isKrone"
				:show-cookies-menu="cookieConsentEnabled"
			/>
		</div>

		<IcsDownloadModal />
		<FeedbackSurvey
			v-if="!$arenaConfig.isSmartTv && $arenaConfig.isAsport && !$arenaConfig.isSwissLeague && !$arenaConfig.isPhygital && isHomePage"
			:user-id="userId"
		/>
		<LoginWallModal />
		<LoginModal />
		<QrLoginModal />
		<ModalCompleteProfile v-if="!$arenaConfig.isSmartTv" />
		<ContactForm
			:contact-email="$arenaConfig.contactEmail"
			:is-swiss-league="$arenaConfig.isSwissLeague"
			:dark-theme="!$arenaConfig.isLightTheme"
			:user-id="userId"
		/>
		<GeoblockingModal />
		<FooterNavMobile />
	</div>
</template>

<script>
import Footer from '@js/components/Footer';
import Header from '@js/components/Header';
import NavMobileTop from '@js/components/NavMobileTop';
import CookieConsent from '@js/components/CookieConsent';
import FeedbackSurvey from '@js/components/FeedbackSurvey';
import ContactForm from '@shared/components/ContactForm';
import IcsDownloadModal from '@js/components/IcsDownloadModal';
import LoginModal from '@js/components/Login/LoginModal';
import QrLoginModal from '@js/components/Login/QrLoginModal';
import ArenaInfo from '@shared/components/ArenaInfo';
import FooterSponsors from '@js/components/FooterSponsors';
import ModalCompleteProfile from '@js/components/ModalCompleteProfile';
import GeoblockingModal from '@js/components/GeoblockingModal';
import LoginWallModal from '@js/components/LoginWallModal';
import FooterNavMobile from '@js/components/FooterNavMobile';
import { ArenaNavigationPlacement } from '@shared/enums/ArenaNavigationPlacement';
import { mapGetters } from 'vuex';

export default {
	name:       'Layout',
	components: {
		Footer,
		Header,
		NavMobileTop,
		CookieConsent,
		ArenaInfo,
		FeedbackSurvey,
		ContactForm,
		LoginModal,
		QrLoginModal,
		IcsDownloadModal,
		SponsorsStv:      () => import('@js/components/SponsorsStv'),
		SponsorsPhygital: () => import('@js/components/SponsorsPhygital'),
		FooterSponsors,
		ModalCompleteProfile,
		GeoblockingModal,
		FooterNavMobile,
		LoginWallModal,
	},
	props: {
		showHeader: {
			type:    Boolean,
			default: true,
		},
		showFooter: {
			type:    Boolean,
			default: true,
		},
		showFooterSponsors: {
			type:    Boolean,
			default: true,
		},
	},
	computed: {
		...mapGetters({
			userId: 'user/userId',
		}),
		cookieConsentEnabled() {
			return this.$arenaConfig.cookieConsent;
		},
		eventCenterEnabled() {
			return !!window.arenaParams?.eventCenterEnabled;
		},
		pathName() {
			return window.location.pathname;
		},
		isHomePage() {
			return window.location.pathname === '/';
		},
		topNavItems() {
			const topNavItems = (this.$arenaConfig.navItems || [])
				.filter(item => item.placement === ArenaNavigationPlacement.TOP)
				.map(item => {
					const link = this.parseNavUrl(item.link);
					return {
						...item,
						isActive: link ? window.location.pathname === link.pathname && window.location.hostname === link.hostname : false,
					};
				});

			topNavItems.push(...this.getExtraTopNavItems());

			return topNavItems;
		},
	},
	methods: {
		parseNavUrl(link) {
			if (!link || typeof link !== 'string') {
				return null;
			}

			try {
				return new URL(link, window.location.origin);
			} catch {
				return null;
			}
		},
		getExtraTopNavItems() {
			const configItems = Array.isArray(this.$arenaConfig.extraTopNavItems)
				? this.$arenaConfig.extraTopNavItems
				: [];

			return configItems
				.map((item, index) => this.normalizeExtraTopNavItem(item, index))
				.filter(Boolean);
		},
		normalizeExtraTopNavItem(extraTopNavItem, index) {
			if (!extraTopNavItem || typeof extraTopNavItem !== 'object') {
				return null;
			}

			const { text, link } = extraTopNavItem;
			if (typeof link !== 'string' || !link.trim() || (!text || (typeof text !== 'string' && typeof text !== 'object'))) {
				return null;
			}

			const title = typeof text === 'object'
				? this.$Helpers.getTranslatedContent(text)
				: this.$I18n.trans(text);
			if (!title || !title.toString().trim()) {
				return null;
			}

			const parsedUrl = this.parseNavUrl(link);
			return {
				id:       `platform-extra-top-nav-${index}`,
				link,
				title,
				isActive: parsedUrl ? window.location.pathname === parsedUrl.pathname && window.location.hostname === parsedUrl.hostname : false,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
.krone-footer {
	background-color: var(--color-body-background);

	&.is-hidden-on-event {
		body.is-player-mode & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			display: none;
		}
	}
}
</style>
